import type { MenuTreeNodeVO } from "@/api/system/menus/type";

/** 侧栏菜单项 */
export interface NavItem {
  name: string;
  href: string;
  /** 菜单图标名（Lucide PascalCase），后端未返回时不展示 */
  icon?: string;
  children?: NavItem[];
  permission?: string;
}

/** 面包屑项 */
export interface BreadcrumbItem {
  name: string;
  href?: string;
}

/** 无菜单时的首页回退路径 */
export const FALLBACK_HOME_PATH = "/403";

/**
 * 将后端菜单树节点转为侧栏 NavItem
 * @param node 菜单树节点
 */
const mapMenuNodeToNavItem = (node: MenuTreeNodeVO): NavItem => {
  const childItems = [...(node.children ?? [])].map(mapMenuNodeToNavItem);
  const item: NavItem = {
    name: node.menuName,
    href: node.routePath ?? "",
    icon: node.icon ?? undefined,
    permission: node.permissionCode,
  };
  if (childItems.length > 0) {
    item.children = childItems;
    if (!node.routePath && childItems[0]) {
      const firstChildHref = childItems[0].href;
      item.href = firstChildHref.substring(0, firstChildHref.lastIndexOf("/")) || firstChildHref;
    }
  }
  return item;
};

/**
 * 将 getUserMenuTree 返回的菜单树转为侧栏配置
 * @param menus 后端菜单树
 */
export const mapMenuTreeToNavItems = (menus: MenuTreeNodeVO[]): NavItem[] => {
  return [...menus].map(mapMenuNodeToNavItem);
};

/**
 * 根据 pathname 从菜单树生成面包屑
 * @param pathname 当前路径
 * @param menus 菜单树
 */
export const getBreadcrumbs = (pathname: string, menus: NavItem[]): BreadcrumbItem[] => {
  for (const item of menus) {
    if (item.children?.length) {
      const child = item.children.find((c) => c.href === pathname);
      if (child) {
        return [{ name: item.name }, { name: child.name, href: child.href }];
      }
    } else if (pathname === item.href) {
      return [{ name: item.name, href: item.href }];
    }
  }
  return [];
};

/**
 * 取菜单第一项的落地路径（登录后 / 的 index 重定向目标）
 * @param menus 菜单树
 */
export const getDefaultHomePath = (menus: NavItem[]): string => {
  const first = menus[0];
  if (!first) {
    return FALLBACK_HOME_PATH;
  }
  if (first.children?.length) {
    return first.children[0].href;
  }
  return first.href;
};

/** 将菜单 href 转为布局下的相对 path（首页为 ''） */
export const toRouteSegment = (href: string): string => {
  if (href === "/") {
    return "";
  }
  return href.replace(/^\//, "");
};

/**
 * 有子菜单的父级路径 → 重定向到第一个子菜单
 * @param menus 菜单树
 */
export const collectParentRedirects = (
  menus: NavItem[],
): { parentSegment: string; childHref: string }[] => {
  const list: { parentSegment: string; childHref: string }[] = [];
  for (const item of menus) {
    if (item.children?.length) {
      list.push({
        parentSegment: toRouteSegment(item.href),
        childHref: item.children[0].href,
      });
    }
  }
  return list;
};

/**
 * 根据当前路径自动展开包含该路径的父级菜单名
 * @param pathname 当前路径
 * @param menus 菜单树
 */
export const getExpandedParentNames = (pathname: string, menus: NavItem[]): string[] => {
  return menus
    .filter((item) =>
      item.children?.some(
        (child) => pathname === child.href || pathname.startsWith(`${child.href}/`),
      ),
    )
    .map((item) => item.name);
};
